<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import { useRoute, useRouter } from 'vue-router';
import { useGenerationStore } from '@/stores/generation';
import { useCreateStore } from '@/stores/create';
import { share, haptic } from '@/composables/useTelegramWebApp';
import MxButton from '@/components/MxButton.vue';
import MxIcon from '@/components/MxIcon.vue';
import MxProgress from '@/components/MxProgress.vue';
import MxVideoPreview from '@/components/MxVideoPreview.vue';

const route = useRoute();
const router = useRouter();
const store = useGenerationStore();
const create = useCreateStore();
const { current, elapsedS, etaMaxS, error } = storeToRefs(store);

const id = computed(() => String(route.params.id));

onMounted(async () => {
  try {
    await store.load(id.value);
    if (current.value && (current.value.status === 'pending' || current.value.status === 'processing')) {
      store.watchProgress(id.value);
    }
  } catch {
    // error already on store
  }
});

onBeforeUnmount(() => store.stopWatch());

const progressPct = computed(() => {
  if (!etaMaxS.value || etaMaxS.value <= 0) return null;
  return Math.min(99, Math.floor((elapsedS.value / etaMaxS.value) * 100));
});

const elapsedLabel = computed(() => {
  const s = elapsedS.value;
  const mm = Math.floor(s / 60);
  const ss = String(s % 60).padStart(2, '0');
  const ema = etaMaxS.value;
  if (!ema || ema <= 0) {
    return `Прошло ${mm}:${ss}`;
  }
  return `Прошло ${mm}:${ss} из ~${Math.ceil(ema / 60)} мин`;
});

async function onShare() {
  if (!current.value?.result_url) return;
  haptic.impact('light');
  await share(current.value.result_url, 'Сгенерировано в Mini App');
}

function onDownload() {
  if (!current.value?.result_url) return;
  haptic.impact('light');
  const a = document.createElement('a');
  a.href = current.value.result_url;
  const ext = current.value.type === 'image' ? 'jpg' : 'mp4';
  a.download = `${current.value.id}.${ext}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

async function onRepeat() {
  const out = await store.repeat();
  if (out) {
    create.loadFromRepeat(out.params);
    const path = out.params.type === 'image' ? '/create/image' : '/create/video';
    await router.push(path);
  }
}

async function onDelete() {
  haptic.notification('warning');
  await store.softDelete();
  await router.push('/history');
}
</script>

<template>
  <section class="gv" v-if="current">
    <header class="hdr">
      <button class="back" @click="router.back()"><MxIcon name="ChevronLeft" /></button>
      <p class="hdr__id">{{ current.id.slice(0, 8) }}</p>
      <button class="more" @click="onDelete"><MxIcon name="Trash2" /></button>
    </header>

    <!-- pending / processing -->
    <div v-if="current.status === 'pending' || current.status === 'processing'" class="state state--prog">
      <div class="prog__shell">
        <div class="prog__shimmer" />
      </div>
      <p class="state__title">Генерируется...</p>
      <MxProgress :value="progressPct" :indeterminate="progressPct === null" />
      <p class="state__sub">{{ elapsedLabel }}</p>
      <p class="state__quote">«{{ current.prompt }}»</p>
    </div>

    <!-- completed -->
    <div v-else-if="current.status === 'completed'" class="state state--done">
      <MxVideoPreview
        :src="current.result_url"
        :poster="current.preview_url"
        :kind="current.type"
        aspect="9 / 16"
      />
      <p class="state__check">✓ Готово</p>
      <div class="actions">
        <button class="act" @click="onDownload"><MxIcon name="Download" /><span>Скачать</span></button>
        <button class="act" @click="onShare"><MxIcon name="Share2" /><span>Поделиться</span></button>
        <button class="act" @click="onRepeat"><MxIcon name="RotateCw" /><span>Повторить</span></button>
      </div>
      <details class="params">
        <summary>Параметры</summary>
        <pre class="params__pre">{{ JSON.stringify(current.params, null, 2) }}</pre>
      </details>
      <p class="state__quote">«{{ current.prompt }}»</p>
    </div>

    <!-- failed -->
    <div v-else class="state state--fail">
      <MxIcon name="XCircle" :size="48" />
      <p class="state__title">Не получилось</p>
      <p class="state__sub">{{ current.error || 'Неизвестная ошибка' }}</p>
      <MxButton variant="primary" @click="onRepeat">Попробовать снова</MxButton>
    </div>
  </section>

  <section v-else-if="error" class="gv">
    <p class="state__sub">{{ error }}</p>
  </section>

  <section v-else class="gv">
    <MxProgress indeterminate />
  </section>
</template>

<style scoped>
.gv { padding: var(--sp-3) 0 var(--sp-12); display: flex; flex-direction: column; gap: var(--sp-4); }
.hdr { display: flex; justify-content: space-between; align-items: center; padding: var(--sp-2) 0; }
.hdr__id { font-family: var(--font-mono); color: var(--text-tertiary); font-size: var(--fs-sm); }
.back, .more { padding: 8px; }
.state { display: flex; flex-direction: column; gap: var(--sp-3); align-items: center; text-align: center; }
.state__title { font-family: var(--font-display); font-size: var(--fs-xl); margin-top: var(--sp-2); }
.state__sub { color: var(--text-secondary); font-size: var(--fs-sm); }
.state__quote { color: var(--text-tertiary); font-style: italic; font-size: var(--fs-sm); padding: 0 var(--sp-4); }
.state__check { color: var(--success); font-weight: 600; opacity: 0; animation: checkfade 2.4s forwards; }
@keyframes checkfade {
  0% { opacity: 0; }
  20% { opacity: 1; }
  80% { opacity: 1; }
  100% { opacity: 0; }
}
.prog__shell { width: 100%; aspect-ratio: 9 / 16; background: var(--bg-overlay); border-radius: var(--r-xl); overflow: hidden; position: relative; }
.prog__shimmer {
  position: absolute; inset: 0;
  background: linear-gradient(110deg, transparent 30%, var(--accent-soft) 50%, transparent 70%);
  background-size: 200% 100%;
  animation: shimmer 1.6s infinite;
}
@keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
.actions { display: grid; grid-template-columns: repeat(3, 1fr); gap: var(--sp-2); width: 100%; }
.act {
  display: flex; flex-direction: column; align-items: center; gap: 6px;
  padding: var(--sp-3) 0;
  background: var(--bg-elevated);
  border-radius: var(--r-md);
  font-size: var(--fs-sm);
  color: var(--text-primary);
  height: 72px;
}
.params { width: 100%; padding: var(--sp-3); background: var(--bg-elevated); border-radius: var(--r-md); }
.params__pre { font-family: var(--font-mono); font-size: var(--fs-xs); color: var(--text-secondary); white-space: pre-wrap; }
</style>
